// Copyright 2021 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import { makeStyles } from "@material-ui/core/styles";
import React, { useState } from "react";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import { useDropzone } from "react-dropzone";
import Box from "@material-ui/core/Box";
import FilesIcon from "@material-ui/icons/FileCopy";
import ClearXerButton from "src/components/loader/buttons/ClearXerButton";
import ConfigurationCheckBox from "eai-configurator-ui/components/configuration/base/ConfigurationCheckBox";
import ConfigurationTextField from "eai-configurator-ui/components/configuration/base/ConfigurationTextField";
import ConfigurationAsyncComboBox from "eai-configurator-ui/components/configuration/base/ConfigurationAsyncComboBox";

const useStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      margin: theme.spacing(1),
    },
  },
  input: {
    display: "none",
  },
  button: {
    margin: 4,
  },
  dropzone: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
  },
  leftColumn: {
    minWidth: "300px",
  },
  rightColumn: {},
}));
const CleanupXerPage = ({
  disabled,
  dropzoneOptions,
  navigateToResults,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);

  const {
    getRootProps,
    getInputProps,
    open: handleOpenFileSelection,
  } = useDropzone({
    // accept: 'image/jpeg, image/png'
    onDrop: (files) => setSelectedFiles(files),
    ...dropzoneOptions,
  });

  const clearSelectedFiles = () => {
    setSelectedFiles([]);
  };

  const selectedFilesItems = selectedFiles.map((file) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ));
  const path = "loader:cleanup";
  const classes = useStyles();
  return (
    <React.Fragment>
      <div>
        <Typography variant="body2" color="textSecondary">
          <h5>Cleanup XER</h5>
          <hr />
        </Typography>
        <div {...getRootProps({ className: classes.dropzone })}>
          <input {...getInputProps()} />
          <p>Drag 'n' drop some files here, or click to select files</p>
        </div>
        <aside>
          <ul>{selectedFilesItems}</ul>
        </aside>
        <Box display="flex" flexDirection="row" justifyContent="flex-end">
          <div>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              startIcon={<FilesIcon />}
              onClick={handleOpenFileSelection}
            >
              Choose File(s)
            </Button>

            <ClearXerButton
              selectedFiles={selectedFiles}
              clearSelectedFiles={clearSelectedFiles}
              disabled={selectedFilesItems.length === 0 || disabled}
              className={classes.button}
              handleSuccess={navigateToResults}
            />
          </div>
        </Box>
        <br />
        <ConfigurationCheckBox path={`${path}:replaceWrongParentWbs`} />
        <Box display="flex" flexDirection="row">
          <Box flexShrink={1} my={0} className={classes.leftColumn}>
            <ConfigurationCheckBox path={`${path}:keepCurrencies`} />
          </Box>
          <Box width={1} mr={1} my={0} className={classes.rightColumn}>
            <ConfigurationAsyncComboBox
              path={`${path}:currencies`}
              freeSolo
              multiple
              textFieldProps={{
                margin: "dense",
                style: { marginBottom: 0, marginTop: 0 },
              }}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="row">
          <Box flexShrink={1} my={0} className={classes.leftColumn}>
            <ConfigurationCheckBox path={`${path}:prefixProjectCodes`} />
          </Box>
          <Box width={1} mr={1} my={0} className={classes.rightColumn}>
            <ConfigurationTextField
              path={`${path}:prefixProjectCodesWith`}
              margin={"dense"}
              className={classes.textField}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="row">
          <Box flexShrink={1} my={0} className={classes.leftColumn}>
            <ConfigurationCheckBox path={`${path}:prefixResourceCodes`} />
          </Box>
          <Box width={1} mr={1} my={0} className={classes.rightColumn}>
            <ConfigurationTextField
              path={`${path}:prefixResourceCodesWith`}
              margin={"dense"}
              className={classes.textField}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="row">
          <Box flexShrink={1} my={0} className={classes.leftColumn}>
            <ConfigurationCheckBox path={`${path}:prefixUDFs`} />
          </Box>
          <Box width={1} mr={1} className={classes.rightColumn}>
            <ConfigurationTextField
              path={`${path}:prefixUDFsWith`}
              margin={"dense"}
              className={classes.textField}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="row">
          <Box flexShrink={1} my={0} className={classes.leftColumn}>
            <ConfigurationCheckBox path={`${path}:prefixGlobalCalendars`} />
          </Box>
          <Box width={1} mr={1} className={classes.rightColumn}>
            <ConfigurationTextField
              path={`${path}:prefixGlobalCalendarsWith`}
              margin={"dense"}
              className={classes.textField}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="row">
          <Box flexShrink={1} my={0} className={classes.leftColumn}>
            <ConfigurationCheckBox path={`${path}:prefixResources`} />
          </Box>
          <Box width={1} mr={1} className={classes.rightColumn}>
            <ConfigurationTextField
              path={`${path}:prefixResourcesWith`}
              margin={"dense"}
              className={classes.textField}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="row">
          <Box flexShrink={1} my={0} className={classes.leftColumn}>
            <ConfigurationCheckBox
              path={`${path}:prefixGlobalActivityCodes`}
            />
          </Box>
          <Box width={1} mr={1} className={classes.rightColumn}>
            <ConfigurationTextField
              path={`${path}:prefixGlobalActivityCodesWith`}
              margin={"dense"}
              className={classes.textField}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="row">
          <Box flexShrink={1} my={0} className={classes.leftColumn}>
            <ConfigurationCheckBox path={`${path}:changeEPSCodeScopes`} />
          </Box>
          <Box width={1} mr={1} className={classes.rightColumn}>
            <ConfigurationTextField
              path={`${path}:changeEPSCodeScopesWith`}
              margin={"dense"}
              className={classes.textField}
            />
          </Box>
        </Box>
        <div/>
      </div>
    </React.Fragment>
  );
};
export default CleanupXerPage;
